import { defineStore } from 'pinia';
import { ref } from 'vue';

type QueueType = 'pageViews' | 'events' | 'consents';
type TrackingQueues = Record<QueueType, any[]>;
export type SessionEnvironmentOrigin =
	| 'seo'
	| 'organic'
	| 'social'
	| 'search'
	| 'direct'
	| 'redirect'
	| 'returning'
	| 'jwtv';

export type PageEntryMode = 'modal';

export type SessionEnvironment = {
	sessionReady: boolean;
	origin: SessionEnvironmentOrigin | null;
	hasAdBlocker: boolean | null;
	originPageUrl: string | null;
	originPageReferred: string | null;
	originRefrMedium: string | null;
	originRefrSource: string | null;
	originRefrSocial: string | null;
	env: string | null;
};

export type OverridePageState = {
	pageType: string;
	pageViewUUID: string;
	pageEntryMode?: 'modal';
} | null;
export const useTrackingStore = defineStore('tracking', () => {
	// SSR SR Campaigns rely on pagesVisted for first page view detection
	const pagesVisited = ref(0);
	const queues = ref<TrackingQueues>({
		pageViews: [],
		events: [],
		consents: [],
	});

	const sessionEnvironment = ref<SessionEnvironment>({
		sessionReady: false,
		origin: null,
		env: null,
		hasAdBlocker: null,
		originPageUrl: null,
		originPageReferred: null,
		originRefrMedium: null,
		originRefrSource: null,
		originRefrSocial: null,
	});

	const pageViewUUID = ref<string | null>(null);
	const overridePageState = ref<OverridePageState>(null);

	function getQueueOf(queueType: QueueType) {
		return queues.value[queueType];
	}

	function setQueues(trackingQueues: TrackingQueues) {
		queues.value = trackingQueues;
	}

	function resetQueue(queueType: QueueType) {
		queues.value[queueType] = [];
	}

	function addToQueue({ queueType, event }: { queueType: QueueType; event: any }) {
		queues.value[queueType].push(event);
	}

	function increasePagesVisited() {
		pagesVisited.value = pagesVisited.value + 1;

		// Update Sentry tag when navigating to second page
		if (process.client && pagesVisited.value === 2) {
			import('@/helpers/sentry-helper').then(({ updateSentryTags }) => {
				updateSentryTags({ first_page: false });
			});
		}
	}

	function addPageView(event: any) {
		const queueType = 'pageViews';
		addToQueue({ queueType, event });
	}

	function addTrackEvent(event: any) {
		const queueType = 'events';
		addToQueue({ queueType, event });
	}

	function setPageViewUUID(UUID: string | null) {
		pageViewUUID.value = UUID;
	}

	function setSessionEnvironment(environment: Partial<SessionEnvironment>) {
		sessionEnvironment.value = { ...sessionEnvironment.value, ...environment };
	}

	function setSessionEnvironmentByKey<S extends keyof SessionEnvironment, V extends SessionEnvironment[S]>({
		key,
		value,
	}: {
		key: S;
		value: V;
	}) {
		sessionEnvironment.value[key] = value;
	}

	function setOverridePageState(pageState: OverridePageState) {
		overridePageState.value = pageState;
	}

	return {
		pagesVisited,
		queues,
		sessionEnvironment,
		pageViewUUID,
		overridePageState,

		getQueueOf,
		setQueues,
		resetQueue,
		addToQueue,
		increasePagesVisited,
		addPageView,
		addTrackEvent,
		setPageViewUUID,
		setSessionEnvironment,
		setSessionEnvironmentByKey,
		setOverridePageState,
	};
});
